import _ from 'lodash';
import { message, notification } from 'antd';
import * as Sentry from '@sentry/react';
import { getAsyncActionResultSelector, updateSpecificState } from '~/hooks/useAsyncActionStatus';
import { TableNameTypes } from '~/hooks/useTracking';
import takeRequest from '~/store/takeRequest';
import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { getLoggedUser } from '~/store/user/actions';
import * as TicketActions from './actions';
import * as UtilitiesActions from '../utilities/actions';

export function* updateTicket(ticketId: string, payload: any) {
  const ticketsResult = yield select(getAsyncActionResultSelector(TicketActions.getTickets));
  const ticketsByUserResult = yield select(getAsyncActionResultSelector(TicketActions.getTicketsByUser));
  const ticketsByCompanyResult = yield select(getAsyncActionResultSelector(TicketActions.getTicketsByCompany));

  const tickets = _.cloneDeep(ticketsResult);
  const updatingTicket = tickets?.tickets?.find((el) => el?.ticket?.id === ticketId);

  const ticketsByUser = Array.isArray(ticketsByUserResult) ? _.cloneDeep(ticketsByUserResult) : null;
  const updatingTicketByUserIndex = ticketsByUser?.findIndex((el) => el?.id === ticketId);

  const ticketsByCompany = Array.isArray(ticketsByCompanyResult) ? _.cloneDeep(ticketsByCompanyResult) : null;
  const updatingTicketByCompanyIndex = ticketsByCompany?.findIndex((el) => el?.id === ticketId);

  if (updatingTicket) {
    updatingTicket.ticket = {
      ...updatingTicket.ticket,
      ...payload,
    };

    yield put(updateSpecificState(TicketActions.getTickets, tickets));
  }

  if (updatingTicketByUserIndex >= 0) {
    ticketsByUser[updatingTicketByUserIndex] = {
      ...ticketsByUser[updatingTicketByUserIndex],
      ...payload,
    };

    yield put(updateSpecificState(TicketActions.getTicketsByUser, ticketsByUser));
  }

  if (updatingTicketByCompanyIndex >= 0) {
    ticketsByCompany[updatingTicketByCompanyIndex] = {
      ...ticketsByCompany[updatingTicketByCompanyIndex],
      ...payload,
    };

    yield put(updateSpecificState(TicketActions.getTicketsByCompany, ticketsByCompany));
  }
}

export function* reloadTicketsList({ meta }) {
  const { personId, companyId } = meta.payload.ticket;

  if (companyId) {
    yield put(TicketActions.getTicketsByCompany.start({ companyId }));
    return;
  }

  if (personId) {
    yield put(TicketActions.getTicketsByUser.start({ personId }));
  }
}

export function* reloadTicketCommentsList({ meta }) {
  const id = meta.payload.ticketId;
  yield put(TicketActions.getTicketComments.start(id, { actionId: id }));
}

export function* updateTicketInListing({ meta, payload }) {
  const { id } = meta.payload;
  yield call(updateTicket, id, payload);
}

export function* updateResponsibleInListing({ payload }) {
  const { idTicket, responsibleId, responsibleName } = payload;
  yield call(updateTicket, idTicket, { responsibleId, responsibleName });
}

export function* updateBatchTicketsInListing({ meta, payload }) {
  const { ids, ticketsId, responsibleId, responsibleName, resolvingAreaId, resolvingArea } = meta?.payload || {};
  const resolvedIds = ids ?? ticketsId;
  if (!resolvedIds?.length) return;

  const results = payload?.results ?? [];
  const successIds =
    results.length > 0 ? results.filter((item) => item?.success).map((item) => item.ticketId) : resolvedIds;

  for (let index = 0; index < successIds.length; index += 1) {
    if (index > 0 && index % 1 === 0) {
      yield delay(0);
    }

    const ticketId = successIds[index];
    const updates: Record<string, any> = {};
    if (typeof responsibleId !== 'undefined') {
      updates.responsibleId = responsibleId;
      updates.responsibleName = responsibleName ?? null;
    }

    if (typeof resolvingAreaId !== 'undefined') {
      updates.resolvingAreaId = resolvingAreaId;
      updates.resolvingArea = resolvingArea;
      updates.responsibleId = null;
      updates.responsibleName = null;
    }

    if (Object.keys(updates).length) {
      yield call(updateTicket, ticketId, updates);
    }
  }
}

export function* updateBatchTicketsInListingDeferred(action) {
  yield delay(0);
  yield call(updateBatchTicketsInListing, action);
}

export function* removeResponsibleInListing({ meta }) {
  const { ticketId } = meta.payload;
  yield call(updateTicket, ticketId, { responsibleId: null, responsibleName: null });
}

export function* reloadTicketFileList({ meta }) {
  const { ticketId, personId } = meta.payload;

  if (personId) {
    yield put(TicketActions.getTicketsByUser.start({ personId }));
  }

  yield put(TicketActions.getTicketDetails.start({ id: ticketId }, { actionId: ticketId }));
  yield put(TicketActions.getTicketFiles.start({ ticketId }));
}

export function* reloadTicket({ meta }) {
  const { ticketId, id } = meta.payload;

  if (ticketId || id) {
    yield put(TicketActions.getTicketDetails.start({ id: ticketId || id }, { actionId: ticketId || id }));
  }
}

export function* sendEventOnChange({ payload }) {
  if (payload?.id) {
    const event = {
      cliente_user_id: payload?.id,
      ação: `flag ${payload?.enabled ? 'ATIVADA' : 'DESATIVADA'} para usuario ${payload?.loggedUser}`,
      título_tela: payload?.tela || '',
      option_id: payload?.optionId || '',
    };

    yield put(
      UtilitiesActions.sendEvent.start({
        tableName: TableNameTypes.BACKOFFICE_FLAG_USER,
        event,
      }),
    );
  }
}

const MAX_EXPORT_POLL_ATTEMPTS = 30; // 30 × 10s = 5 minutes per mount; resume picks up later if still pending
const MAX_CONSECUTIVE_FAILURES = 5; // bail out after 5 consecutive non-404 errors to avoid forever loops

function safeSetPendingJob(jobId: string, userId: number | string | undefined) {
  try {
    const value = JSON.stringify({ jobId, userId: userId ?? null });
    localStorage.setItem(TicketActions.EXPORT_PENDING_JOB_KEY, value);
  } catch (e) {
    // localStorage unavailable (private mode, quota) — proceed without persistence
    Sentry.captureException(e, { tags: { feature: 'ticket-export', step: 'persist-job' } });
  }
}

function safeClearPendingJob() {
  try {
    localStorage.removeItem(TicketActions.EXPORT_PENDING_JOB_KEY);
  } catch (e) {
    Sentry.captureException(e, { tags: { feature: 'ticket-export', step: 'clear-job' } });
  }
}

function notifyDownloadReady(downloadUrl: string) {
  let popup: Window | null = null;
  try {
    popup = window.open(downloadUrl, '_blank');
  } catch (_e) {
    popup = null;
  }
  // Browsers return null/undefined when the popup is blocked
  if (!popup) {
    notification.success({
      message: 'Arquivo pronto para download',
      description: 'O download automático foi bloqueado pelo navegador. Clique aqui para baixar.',
      duration: 0,
      onClick: () => window.open(downloadUrl, '_blank'),
      key: 'ticket-export-ready',
    });
  } else {
    message.success('Arquivo pronto para download');
  }
}

export function* pollExportStatus({ payload }) {
  const { jobId } = payload;
  const loggedUser = yield select(getAsyncActionResultSelector(getLoggedUser));
  safeSetPendingJob(jobId, loggedUser?.id);
  let attempts = 0;
  let consecutiveFailures = 0;
  try {
    while (attempts < MAX_EXPORT_POLL_ATTEMPTS) {
      yield delay(10000);
      attempts += 1;
      yield put(TicketActions.getExportStatus.start({ jobId, userId: loggedUser?.id }));
      const result = yield race({
        success: take(TicketActions.getExportStatus.success.toString()),
        failure: take(TicketActions.getExportStatus.failure.toString()),
      });

      // Job not found / deleted on backend — abandon the persisted jobId to avoid an infinite resume loop
      if (result.failure?.payload?.status === 404) {
        message.error('Exportação não encontrada. Inicie uma nova exportação.');
        safeClearPendingJob();
        return;
      }

      if (result.failure) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          message.error('Falha persistente ao consultar o status. Tente novamente mais tarde.');
          safeClearPendingJob();
          return;
        }
        // Transient error — keep looping
      } else {
        consecutiveFailures = 0;

        if (result.success?.payload?.status === 'COMPLETED') {
          notifyDownloadReady(result.success.payload.downloadUrl);
          safeClearPendingJob();
          return;
        }
        if (result.success?.payload?.status === 'FAILED') {
          message.error('Falha ao gerar o arquivo. Tente novamente.');
          safeClearPendingJob();
          return;
        }
        // PROCESSING → loop continues
      }
    }
    // Max attempts reached — keep jobId persisted so the user can resume on next mount
    message.info(
      'A exportação ainda está em andamento. Volte a esta tela em alguns instantes para verificar o progresso.',
    );
  } finally {
    // takeLatest cancellation — keep the jobId persisted so the user can resume on next mount
  }
}

export function* ticketSaga() {
  yield takeRequest(TicketActions.getServiceChannels);
  yield takeRequest(TicketActions.getReasons);
  yield takeRequest(TicketActions.createTicket);
  yield takeRequest(TicketActions.createTicketComment);
  yield takeRequest(TicketActions.getTicketUsers);
  yield takeRequest(TicketActions.getTicketsByUser);
  yield takeRequest(TicketActions.getTicketsByCompany);
  yield takeRequest(TicketActions.getTicketTeams);
  yield takeRequest(TicketActions.getTicketDetails);
  yield takeRequest(TicketActions.getTicketComments);
  yield takeRequest(TicketActions.getCommentTypes);
  yield takeRequest(TicketActions.updateTicketComment);
  yield takeRequest(TicketActions.updateTicket);
  yield takeRequest(TicketActions.getTicketResolvingAreas);
  yield takeRequest(TicketActions.getTicketStatus);
  yield takeRequest(TicketActions.getTicketAccompaniments);
  yield takeRequest(TicketActions.hasLinkedTicket);
  yield takeRequest(TicketActions.selectTicket);
  yield takeRequest(TicketActions.getTicketProducts);
  yield takeRequest(TicketActions.getTicketJourneys);
  yield takeRequest(TicketActions.getTicketResolutionReasons);
  yield takeRequest(TicketActions.getTicketJourneysSteps);
  yield takeRequest(TicketActions.getTickets);
  yield takeRequest(TicketActions.getFlatTickets);
  yield takeRequest(TicketActions.selectAssessment);
  yield takeRequest(TicketActions.getExperts);
  yield takeRequest(TicketActions.createTicketAssessment);
  yield takeRequest(TicketActions.updateTicketAssessment);
  yield takeRequest(TicketActions.getTicketMessages);
  yield takeRequest(TicketActions.getConciergeMessages);
  yield takeRequest(TicketActions.getTicketAssessment);
  yield takeRequest(TicketActions.getTicketMedia);
  yield takeRequest(TicketActions.getTicketResponsibles);
  yield takeRequest(TicketActions.removeResponsible);
  yield takeRequest(TicketActions.updateResponsible);
  yield takeRequest(TicketActions.updateBatchTicket);
  yield takeRequest(TicketActions.getTicketStatusGroup);
  yield takeRequest(TicketActions.getTicketReasons);
  yield takeRequest(TicketActions.getTicketSupportService);
  yield takeRequest(TicketActions.createTicketSupportService);
  yield takeRequest(TicketActions.createTicketFiles);
  yield takeRequest(TicketActions.getTicketFiles);
  yield takeRequest(TicketActions.getBatchImportTemplate);
  yield takeRequest(TicketActions.importBatchExecution);
  yield takeRequest(TicketActions.getBatchExecutions);
  yield takeRequest(TicketActions.getBatchCreationTemplate);
  yield takeRequest(TicketActions.importBatchCreationExecution);
  yield takeRequest(TicketActions.getBatchCreationExecutions);

  yield takeRequest(TicketActions.getTicketDistributionConfig);
  yield takeRequest(TicketActions.updateTicketDistributionFlag);
  yield takeRequest(TicketActions.getTicketCategories);
  yield takeRequest(TicketActions.getTicketClassificationRules);
  yield takeRequest(TicketActions.getTicketDashboard);
  yield takeRequest(TicketActions.getTicketDashboardAgents);
  yield takeRequest(TicketActions.exportTickets);
  yield takeRequest(TicketActions.getExportStatus);
  yield takeLatest(TicketActions.exportTickets.success.toString(), pollExportStatus);
  yield takeLatest<any>(TicketActions.RESUME_EXPORT_POLLING, pollExportStatus);
  yield takeLatest(TicketActions.createTicket.success.toString(), reloadTicketsList);
  yield takeLatest(TicketActions.updateTicket.success.toString(), reloadTicket);
  yield takeLatest(TicketActions.createTicketComment.success.toString(), reloadTicketCommentsList);
  yield takeLatest(TicketActions.updateTicketComment.success.toString(), reloadTicketCommentsList);
  yield takeLatest(TicketActions.createTicketFiles.success.toString(), reloadTicketFileList);
  yield takeLatest(TicketActions.updateResponsible.success.toString(), updateResponsibleInListing);
  yield takeLatest(TicketActions.updateBatchTicket.success.toString(), updateBatchTicketsInListingDeferred);
  yield takeLatest(TicketActions.removeResponsible.success.toString(), removeResponsibleInListing);
  yield takeLatest(TicketActions.getTicketDetails.success.toString(), updateTicketInListing);
  yield takeLatest(TicketActions.createTicketSupportService.success.toString(), reloadTicket);
  yield takeLatest(TicketActions.updateTicketDistributionFlag.start.toString(), sendEventOnChange);
}
